import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom, filter } from 'rxjs/operators';
import * as FedexFacilityActions from '../actions/fedex-facility.actions';
import { SpotsService } from '../services/spots.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';
import { FedexFacility } from '../models/fedex-facility.model';
import * as UiActions from '../../ui/actions/ui.actions';

@Injectable()
export class FedexFacilityEffects {
  loadFedexFacilities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FedexFacilityActions.loadFedexFacilities),
      withLatestFrom(this.store.select(state => state.spots.fedexFacilities)),
      filter(([action, facilities]) => !facilities || facilities.length === 0), // only continue if it was not loaded before
      switchMap(([action, facilities]) => {
        const { organizationId } = action;
        return this.spotsService.getFedexFacilities(organizationId).pipe(
          map(facilities => FedexFacilityActions.loadFedexFacilitiesSuccess({ facilities })),
          catchError(error => of(FedexFacilityActions.loadFedexFacilitiesFailure({ error })))
        )
      })
    )
  );

  addFedexFacility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FedexFacilityActions.addFedexFacility),
      switchMap(({ organizationId, facility }) => {
        return this.spotsService.addFedexFacility(organizationId, facility).pipe(
          map((facility: FedexFacility) => FedexFacilityActions.addFedexFacilitySuccess({ facility })),
          catchError(error => of(UiActions.serviceCallFailure({ action: FedexFacilityActions.addFedexFacility, error })))
        )
      })
    )
  );

  updateFedexFacility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FedexFacilityActions.updateFedexFacility),
      switchMap(({ organizationId, facilityId, facility }) => {
        return this.spotsService.updateFedexFacility(organizationId, facilityId, facility).pipe(
          map((facility: FedexFacility) => FedexFacilityActions.updateFedexFacilitySuccess({ facility })),
          catchError(error => of(UiActions.serviceCallFailure({ action: FedexFacilityActions.updateFedexFacility, error })))
        )
      })
    )
  );

  deleteFedexFacility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FedexFacilityActions.deleteFedexFacility),
      switchMap(({ organizationId, facilityId }) => {
        return this.spotsService.deleteFedexFacility(organizationId, facilityId).pipe(
          map(() => FedexFacilityActions.deleteFedexFacilitySuccess({ facilityId })),
          catchError(error => of(UiActions.serviceCallFailure({ action: FedexFacilityActions.deleteFedexFacility, error })))
        )
      })
    )
  );

  constructor(
    private actions$: Actions,
    private spotsService: SpotsService,
    private store: Store<AppState>
  ) {}
}
